import { type MouseEvent, useState } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

export type StackedBarSegment = Readonly<{
  key: string;
  pct: number;
  color: ColorToken;
  labelColor: ColorToken;
  /** Shown in a tooltip while the segment is hovered. */
  tooltip?: string;
  id?: string;
  onClick?: () => void;
}>;

export type StackedBarProps = Readonly<{
  segments: readonly StackedBarSegment[];
  height: number;
}>;

/** One horizontal bar split into percent segments; the percentages show on hover, and each segment's tooltip if given. */
export const StackedBar = ({ segments, height }: StackedBarProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const total = segments.reduce((sum, segment) => sum + segment.pct, 0);
  const lastVisibleIndex = segments.reduce(
    (last, segment, index) => (segment.pct > 0 ? index : last),
    -1,
  );

  return (
    <Box
      sx={trackSx}
      style={{ height }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {segments.map((segment, index) => {
        const box = (
          <Box
            key={segment.key}
            id={segment.id}
            sx={segmentSx(segment.pct > 0 && index < lastVisibleIndex, Boolean(segment.onClick))}
            onClick={
              segment.onClick &&
              ((event: MouseEvent) => {
                event.stopPropagation();
                segment.onClick?.();
              })
            }
            style={{
              width: `${total > 0 ? (segment.pct / total) * 100 : 0}%`,
              backgroundColor: COLOR_TOKENS[segment.color],
            }}
          >
            <Typography
              variant="text-xs-medium"
              sx={segmentLabelSx}
              style={{ color: COLOR_TOKENS[segment.labelColor], opacity: isHovered ? 1 : 0 }}
            >
              {`${segment.pct}%`}
            </Typography>
          </Box>
        );

        return segment.tooltip ? (
          <Tooltip key={segment.key} arrow placement="top" title={segment.tooltip}>
            {box}
          </Tooltip>
        ) : (
          box
        );
      })}
    </Box>
  );
};

const trackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: '100%',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
});

const segmentSx =
  (hasSeparator: boolean, isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    cursor: isClickable ? 'pointer' : 'default',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minWidth: 0,
    overflow: 'hidden',
    borderRight: hasSeparator ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const segmentLabelSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: backrs.spacing['spacing-sm'],
  whiteSpace: 'nowrap',
  transition: 'opacity 0.2s ease-in-out',
});
